import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { Currency } from "../../../constants";
import {
  formatCurrencyInput,
  parseCurrencyInput,
} from "../../../utils/currency";

interface CurrencyExchangeInputProps {
  baseCurrency: Currency;
  quoteCurrency: Currency;
  baseAmount: string;
  quoteAmount: string;
  exchangeRate: number | null;
  fee: number;
  isLoading: boolean;
  onBaseAmountChange: (amount: string) => void;
  onQuoteAmountChange: (amount: string) => void;
  onBaseCurrencyClick?: () => void;
  onQuoteCurrencyClick?: () => void;
  onSwapCurrencies?: () => void;
  error?: string;
  minAmount?: number;
  maxAmount?: number;
  balance: number;
  activeInput?: "base" | "quote";
  canChangeBaseCurrency?: boolean;
  canChangeQuoteCurrency?: boolean;
}

export function CurrencyExchangeInput({
  baseCurrency = Currency.NGN,
  quoteCurrency = Currency.KES,
  baseAmount,
  quoteAmount,
  exchangeRate,
  fee,
  isLoading,
  onBaseAmountChange,
  onQuoteAmountChange,
  onBaseCurrencyClick,
  onQuoteCurrencyClick,
  onSwapCurrencies,
  error,
  balance,
  activeInput = "base",
  canChangeBaseCurrency = false,
  canChangeQuoteCurrency = true,
}: CurrencyExchangeInputProps) {
  const handleBaseInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const cleaned = parseCurrencyInput(rawValue);
    onBaseAmountChange(cleaned);
  };

  const handleQuoteInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const cleaned = parseCurrencyInput(rawValue);
    onQuoteAmountChange(cleaned);
  };

  const formattedBaseAmount = baseAmount ? formatCurrencyInput(baseAmount) : "";
  const formattedQuoteAmount = quoteAmount
    ? formatCurrencyInput(quoteAmount)
    : "";

  return (
    <div className="currency-exchange-input space-y-4">
      {/* Base Currency Input */}
      <div className="input-section">
        <label className="block text-sm text-gray-600 mb-2">You Send</label>

        <div
          className={`flex items-center border rounded-lg p-3 transition-all ${
            activeInput === "base"
              ? "border-primary ring-2 ring-primary/20"
              : "border-gray-300"
          } ${error ? "border-red-500" : ""}`}
        >
          <input
            type="text"
            inputMode="numeric"
            value={formattedBaseAmount}
            onChange={handleBaseInputChange}
            placeholder="0"
            className="flex-1 text-lg font-semibold outline-none bg-transparent"
            disabled={isLoading && activeInput === "quote"}
          />

          <button
            type="button"
            onClick={onBaseCurrencyClick}
            disabled={!canChangeBaseCurrency}
            className={`ml-2 flex items-center gap-1 px-2 py-1 rounded-md transition-colors ${
              canChangeBaseCurrency
                ? "hover:bg-gray-100 cursor-pointer"
                : "cursor-default"
            }`}
          >
            <span className="text-gray-700 font-medium">{baseCurrency}</span>
            {canChangeBaseCurrency && (
              <ChevronDownIcon className="w-4 h-4 text-gray-500" />
            )}
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-1">
          Balance: {balance.toLocaleString("en-US")} {baseCurrency}
        </p>

        {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
      </div>

      {/* Swap Button */}
      {onSwapCurrencies && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={onSwapCurrencies}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Swap currencies"
          >
            <svg
              className="w-5 h-5 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Quote Currency Input */}
      <div className="input-section">
        <label className="block text-sm text-gray-600 mb-2">
          Recipient Gets
        </label>

        <div
          className={`flex items-center border rounded-lg p-3 transition-all relative ${
            activeInput === "quote"
              ? "border-primary ring-2 ring-primary/20"
              : "border-gray-300"
          }`}
        >
          {isLoading && activeInput === "base" && (
            <div className="absolute inset-0 bg-white/50 rounded-lg flex items-center justify-center">
              <div className="animate-pulse h-6 bg-gray-200 rounded w-24" />
            </div>
          )}

          <input
            type="text"
            inputMode="numeric"
            value={formattedQuoteAmount}
            onChange={handleQuoteInputChange}
            placeholder="0"
            className="flex-1 text-lg font-semibold outline-none bg-transparent"
            disabled={isLoading && activeInput === "base"}
          />

          <button
            type="button"
            onClick={onQuoteCurrencyClick}
            disabled={!canChangeQuoteCurrency}
            className={`ml-2 flex items-center gap-1 px-2 py-1 rounded-md transition-colors ${
              canChangeQuoteCurrency
                ? "hover:bg-gray-100 cursor-pointer"
                : "cursor-default"
            }`}
          >
            <span className="text-gray-700 font-medium">{quoteCurrency}</span>
            {canChangeQuoteCurrency && (
              <ChevronDownIcon className="w-4 h-4 text-gray-500" />
            )}
          </button>
        </div>

        {exchangeRate && (
          <p className="text-xs text-gray-500 mt-1">
            Fee: {fee.toLocaleString("en-US")} {quoteCurrency} deducted from
            recipient amount
          </p>
        )}
      </div>
    </div>
  );
}
